import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import axios from "axios";
import Select from "react-select";
import {addPupilsToTargetGroup} from "api/targetGroups/addPupilsToTargetGroup";
import {getSchools} from "redux/actions/setSchoolActions";
import {useFetchPupilsByTargetGroup} from "hooks/useQuery/useFetchPupilsByTargetGroup";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        padding: "0.25rem",
        border: "1px solid #ddd",
        borderRadius: "8px",
        fontSize: "16px",
        borderColor: state.isFocused ? "#4a90e2" : "#ddd",
        boxShadow: "none",
        "&:hover": {
            borderColor: state.isFocused ? "#4a90e2" : "#ddd",
        },
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: "16px",
        padding: "0.75rem",
        backgroundColor: state.isSelected ? "#4a90e2" : state.isFocused ? "#f5f5f5" : "white",
        color: state.isSelected ? "white" : "#122443",
        cursor: "pointer",
    }),
    placeholder: (provided) => ({
        ...provided,
        color: "#999",
    }),
    singleValue: (provided) => ({
        ...provided,
        color: "#122443",
    }),
};

export const AddPupilsToTargetGroupModal = ({ groupId, groupSchoolId, onClose }) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { schools } = useSelector((state) => state.school);
    const { pupils: pupilsInGroup } = useFetchPupilsByTargetGroup(groupId);
    const [selectedClassId, setSelectedClassId] = useState("");
    const [selectedPupilIds, setSelectedPupilIds] = useState([]);
    const [classes, setClasses] = useState([]);
    const [pupils, setPupils] = useState([]);
    const [isLoadingClasses, setIsLoadingClasses] = useState(false);
    const [isLoadingPupils, setIsLoadingPupils] = useState(false);

    useEffect(() => {
        if (!schools?.length) {
            dispatch(getSchools());
        }
    }, [dispatch, schools]);

    useEffect(() => {
        const loadClasses = async () => {
            if (!groupSchoolId) {
                setClasses([]);
                return;
            }

            setIsLoadingClasses(true);

            try {
                const response = await axios.get(`/schools/${groupSchoolId}/classes`);
                setClasses(response.data);
            } catch (error) {
                console.error("Ошибка загрузки классов:", error);
                setClasses([]);
            } finally {
                setIsLoadingClasses(false);
            }
        };

        loadClasses();
    }, [groupSchoolId]);

    const addMutation = useMutation({
        mutationFn: () => addPupilsToTargetGroup(groupId, selectedPupilIds),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["targetGroups/level1"] });
            await queryClient.invalidateQueries({ queryKey: ["targetGroups/level2"] });
            onClose();
        },
    });

    const handleClassChange = async (selectedOption) => {
        const classId = selectedOption?.value || "";
        setSelectedClassId(classId);
        setSelectedPupilIds([]);

        if (!classId) {
            setPupils([]);
            return;
        }

        setIsLoadingPupils(true);

        try {
            const response = await axios.get(`/classes/${classId}/pupils`);
            const allPupils = Array.isArray(response.data) ? response.data : [];
            const pupilIdsInGroup = pupilsInGroup.map((pupil) => pupil._id);
            const availablePupils = allPupils.filter((pupil) => !pupilIdsInGroup.includes(pupil._id));
            setPupils(availablePupils);
        } catch (error) {
            console.error("Ошибка загрузки учеников:", error);
            setPupils([]);
        } finally {
            setIsLoadingPupils(false);
        }
    };

    const handlePupilToggle = (pupilId) => {
        setSelectedPupilIds((prev) =>
            prev.includes(pupilId)
                ? prev.filter((id) => id !== pupilId)
                : [...prev, pupilId],
        );
    };

    const handleSelectAll = () => {
        if (selectedPupilIds.length === pupils.length) {
            setSelectedPupilIds([]);
        } else {
            setSelectedPupilIds(pupils.map((pupil) => pupil._id));
        }
    };

    const handleSubmit = () => {
        if (selectedPupilIds.length > 0) {
            addMutation.mutate();
        }
    };

    const schoolName = useMemo(
        () => schools.find((school) => school._id === groupSchoolId)?.name || "Школа группы",
        [groupSchoolId, schools],
    );

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(event) => event.stopPropagation()}>
                <div className="modal-header">
                    <h2>Добавить учеников в целевую группу</h2>
                    <button className="modal-close" onClick={onClose}>
                        x
                    </button>
                </div>

                <div className="modal-body" style={{ minHeight: "500px" }}>
                    <div className="form-group">
                        <label>Школа группы:</label>
                        <div
                            style={{
                                padding: "0.75rem 1rem",
                                border: "1px solid #ddd",
                                borderRadius: "8px",
                                backgroundColor: "#f9f9f9",
                            }}
                        >
                            {schoolName}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Выберите класс:</label>
                        <Select
                            value={
                                classes.find((item) => item._id === selectedClassId)
                                    ? {
                                        value: selectedClassId,
                                        label: `${classes.find((item) => item._id === selectedClassId).number} ${classes.find((item) => item._id === selectedClassId).letter}`,
                                    }
                                    : null
                            }
                            onChange={handleClassChange}
                            options={classes.map((item) => ({
                                value: item._id,
                                label: `${item.number} ${item.letter}`,
                            }))}
                            placeholder="-- Выберите класс --"
                            isClearable
                            isSearchable
                            isDisabled={isLoadingClasses || !groupSchoolId}
                            isLoading={isLoadingClasses}
                            styles={customSelectStyles}
                        />
                    </div>

                    {selectedClassId && (
                        <div className="form-group">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <label>Выберите учеников:</label>
                                {pupils.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn_secondary btn_small"
                                        onClick={handleSelectAll}
                                    >
                                        {selectedPupilIds.length === pupils.length ? "Снять все" : "Выбрать все"}
                                    </button>
                                )}
                            </div>

                            {isLoadingPupils ? (
                                <p>Загрузка учеников...</p>
                            ) : pupils.length > 0 ? (
                                <div className="pupils-list">
                                    {pupils.map((pupil) => (
                                        <label key={pupil._id} className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={selectedPupilIds.includes(pupil._id)}
                                                onChange={() => handlePupilToggle(pupil._id)}
                                            />
                                            <span>
                                                {pupil.surname} {pupil.name} {pupil.patronymic}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p>В этом классе нет учеников</p>
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn btn_secondary" onClick={onClose}>
                        Отмена
                    </button>
                    <button
                        className="btn btn_primary"
                        onClick={handleSubmit}
                        disabled={selectedPupilIds.length === 0 || addMutation.isLoading}
                    >
                        {addMutation.isLoading ? "Сохранение..." : `Сохранить (${selectedPupilIds.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
};
